import { useState } from "react";
import { Card } from "@/components/ui/card";
import { CreditCard } from "lucide-react";
import { eur, num } from "@/lib/format";

const Bar = ({ pct, tone }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
    <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.min(pct, 100)}%` }} />
  </div>
);

const CHANNELS = [["tutti", "Tutti"], ["negozio", "Negozio"], ["online", "Online"]];

const ChannelTabs = ({ value, onChange, counts }) => (
  <div className="mb-4 inline-flex rounded-md border border-border p-0.5" data-testid="payment-channel-tabs">
    {CHANNELS.map(([k, l]) => (
      <button key={k} type="button" onClick={() => onChange(k)} data-testid={`payment-channel-${k}`}
        className={`rounded px-3 py-1 text-xs font-medium transition-colors ${value === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
        {l} <span className="opacity-70">· {num(counts[k] || 0)}</span>
      </button>
    ))}
  </div>
);

export function PaymentMethods({ data: all, showMoney }) {
  const [channel, setChannel] = useState("tutti");
  const data = all?.[channel];
  const methods = data?.methods || [];
  const counts = Object.fromEntries(CHANNELS.map(([k]) => [k, all?.[k]?.orders]));
  const topAvg = Math.max(0, ...methods.filter((m) => m.orders >= 5).map((m) => m.avg_order_value || 0));
  return (
    <Card className="p-6" data-testid="payment-methods-card">
      <div className="mb-1 flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold">Metodi di pagamento</h3>
        <CreditCard className="h-4 w-4 text-primary" />
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Su {num(data?.orders || 0)} ordini del periodo{channel === "tutti" ? "" : ` fatti ${channel === "negozio" ? "in negozio" : "online"}`}. Se un ordine usa anche credito negozio o carta regalo conta nel metodo principale.
      </p>
      <ChannelTabs value={channel} onChange={setChannel} counts={counts} />
      {methods.length === 0 && <p className="text-sm text-muted-foreground">Nessun ordine nel periodo.</p>}
      <div className={`mb-2 grid gap-3 text-[11px] uppercase tracking-widest text-muted-foreground font-mono-erp ${showMoney ? "grid-cols-[minmax(0,1fr)_5.5rem_5.5rem] sm:grid-cols-[minmax(0,1fr)_7rem_7rem]" : "grid-cols-[minmax(0,1fr)_7rem]"}`}>
        <span>Metodo</span><span className="text-right">Ordini</span>{showMoney && <span className="text-right">Incasso</span>}
      </div>
      <div className="space-y-3">
        {methods.map((m, i) => (
          <div key={m.name} className={`grid items-center gap-3 ${showMoney ? "grid-cols-[minmax(0,1fr)_5.5rem_5.5rem] sm:grid-cols-[minmax(0,1fr)_7rem_7rem]" : "grid-cols-[minmax(0,1fr)_7rem]"}`} data-testid={`payment-method-${i}`}>
            <div className="min-w-0">
              <span className="block text-sm font-medium leading-tight">{m.name}</span>
              {showMoney && m.avg_order_value > 0 && (
                <p className="mt-0.5 text-xs text-muted-foreground" data-testid={`payment-method-${i}-avg`}>
                  Scontrino medio <span className="font-semibold text-foreground">{eur(m.avg_order_value)}</span>
                  {topAvg > 0 && m.avg_order_value === topAvg && (
                    <span className="ml-1.5 inline-block whitespace-nowrap rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-medium text-primary" data-testid="payment-top-spender">spende di più</span>
                  )}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold" data-testid={`payment-method-${i}-orders-pct`}>{num(m.orders_pct)}%</p>
              <p className="text-[11px] text-muted-foreground">{num(m.orders)} ordini</p>
              <Bar pct={m.orders_pct} tone="bg-primary" />
            </div>
            {showMoney && (
              <div className="text-right">
                <p className="text-sm font-semibold" data-testid={`payment-method-${i}-revenue-pct`}>{num(m.revenue_pct)}%</p>
                <p className="text-[11px] text-muted-foreground">{eur(m.revenue)}</p>
                <Bar pct={m.revenue_pct} tone="bg-emerald-500" />
              </div>
            )}
          </div>
        ))}
      </div>
      {data?.with_credit > 0 && (
        <p className="mt-4 text-xs text-muted-foreground" data-testid="payment-with-credit">
          {num(data.with_credit_pct)}% degli ordini ({num(data.with_credit)}) ha usato anche credito negozio o carta regalo.
        </p>
      )}
    </Card>
  );
}
